import React, { Component } from 'react'
import { withFirebase } from '../../components/Fb'
import { Row, Col, Button } from 'reactstrap'
import {
  DataGrid,
  GridToolbarExport,
  GridToolbarContainer,
  GridToolbarFilterButton,
} from '@mui/x-data-grid'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import FalconModal from '../../components/Inputs/FalconModal'
import EditButton from '../../components/Buttons/EditButton'
import DeleteButton from '../../components/Buttons/DeleteButton'
import ShareRegistryInfo from '../../components/Infos/ShareRegistryInfo'
import {
  getShareClassSchema,
  getShareClassUiSchema,
  getShareTemplateSchema,
  getShareTemplateUiSchema,
  getShareCertificateSchema,
  getShareCertificateUiSchema,
} from '../../components/Inputs/dataschemas'

const service = 'shareregistry'

function ExportToolbar({ suffix }) {
  return (
    <GridToolbarContainer style={{ justifyContent: 'flex-start' }}>
      <GridToolbarFilterButton />
      <GridToolbarExport
        csvOptions={{
          fileName: 'osakerekisteri-' + (suffix || 'export'),
          delimiter: ';',
          utf8WithBom: true,
        }}
      />
    </GridToolbarContainer>
  )
}

class ShareRegistrySettingsContainer extends Component {
  constructor(props) {
    super(props)
    this.state = {
      enabled: false,
      saving: false,
      loading: true,
      activeTab: 0,
      shares: [],
      classes: [],
      templates: [],
      certificates: [],
      editModalOpen: false,
      selectedResource: null,
      currentEntityType: 'class',
      // Batch creation dialog state
      batchDialogOpen: false,
      batchForm: { firstNumber: '', lastNumber: '', templateId: '' },
    }
  }

  componentDidMount() {
    if (this.props.modules && this.props.modules[service] && this.props.modules[service].enabled) {
      this.setState({ enabled: true })
      this.loadAllData()
    } else {
      this.setState({ loading: false })
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.modules && this.props.modules[service]) {
      if (this.props.modules[service].enabled && !prevProps.modules[service]?.enabled) {
        this.setState({ enabled: true })
        this.loadAllData()
      }
      if (!this.props.modules[service].enabled && prevProps.modules[service]?.enabled) {
        this.setState({ enabled: false })
      }
    } else {
      if (this.state.enabled) {
        this.setState({ enabled: false })
      }
    }
  }

  enableModule = async (price) => {
    this.setState({ saving: true })
    await this.props.firebase.enableModule(service, price)
  }

  disableModule = async () => {
    if (!window.confirm('Haluatko varmasti poistaa osakerekisterin käytöstä?')) return
    await this.props.firebase.disableModule(service)
  }

  loadAllData = async () => {
    try {
      const [classesSnap, sharesSnap, templatesSnap, certificatesSnap] = await Promise.all([
        this.props.firebase.getShareClasses(),
        this.props.firebase.getShares(),
        this.props.firebase.getShareTemplates(),
        this.props.firebase.getShareCertificates(),
      ])

      const classes = classesSnap.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((r) => r.state !== 'deleted')

      const shares = sharesSnap.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((r) => r.state !== 'deleted')

      const templates = templatesSnap.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((r) => r.state !== 'deleted')

      const certificates = certificatesSnap.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((r) => r.state !== 'deleted')

      this.setState({ classes, shares, templates, certificates, loading: false })
    } catch (error) {
      console.error('Error loading share registry settings:', error)
      this.setState({ loading: false })
    }
  }

  handleTabChange = (event, newValue) => {
    this.setState({ activeTab: newValue })
  }

  toggleModal = () => {
    this.setState({ editModalOpen: !this.state.editModalOpen })
  }

  handleAdd = (entityType) => {
    this.setState(
      { selectedResource: {}, currentEntityType: entityType },
      this.toggleModal
    )
  }

  handleEdit = (id, entityType) => {
    let collection
    if (entityType === 'template') collection = this.state.templates
    else if (entityType === 'certificate') collection = this.state.certificates
    else collection = this.state.classes

    const selected = collection.find((r) => r.id === id)
    this.setState(
      { selectedResource: { ...selected }, currentEntityType: entityType },
      this.toggleModal
    )
  }

  handleDelete = async (id, entityType) => {
    if (entityType === 'template') await this.props.firebase.deleteShareTemplate(id)
    else if (entityType === 'certificate') await this.props.firebase.deleteShareCertificate(id)
    else await this.props.firebase.deleteShareClass(id)
    this.loadAllData()
  }

  getCreateFn = () => {
    const { currentEntityType } = this.state
    if (currentEntityType === 'template') return this.props.firebase.createShareTemplate
    if (currentEntityType === 'certificate') return this.props.firebase.createShareCertificate
    return this.props.firebase.createShareClass
  }

  getUpdateFn = () => {
    const { currentEntityType } = this.state
    if (currentEntityType === 'template') return this.props.firebase.updateShareTemplate
    if (currentEntityType === 'certificate') return this.props.firebase.updateShareCertificate
    return this.props.firebase.updateShareClass
  }

  getSchema = () => {
    const { currentEntityType } = this.state
    if (currentEntityType === 'template') return this.getEnhancedTemplateSchema()
    if (currentEntityType === 'certificate') return this.getEnhancedCertificateSchema()
    return getShareClassSchema()
  }

  getUiSchema = () => {
    const { currentEntityType } = this.state
    if (currentEntityType === 'template') return getShareTemplateUiSchema()
    if (currentEntityType === 'certificate') return getShareCertificateUiSchema()
    return getShareClassUiSchema()
  }

  getModalTitle = () => {
    const { currentEntityType, selectedResource } = this.state
    const isNew = !selectedResource || !selectedResource.id
    if (currentEntityType === 'template') return isNew ? 'Uusi osakepohja' : 'Muokkaa osakepohjaa'
    if (currentEntityType === 'certificate') return isNew ? 'Uusi osakekirja' : 'Muokkaa osakekirjaa'
    return isNew ? 'Uusi osakelajit' : 'Muokkaa osakelajia'
  }

  getEnhancedTemplateSchema = () => {
    const schema = getShareTemplateSchema()
    const { classes } = this.state

    if (classes.length > 0) {
      delete schema.properties.classId.enum
      delete schema.properties.classId.enumNames
      schema.properties.classId.oneOf = classes.map((c) => ({
        const: c.id,
        title: c.name || c.id,
      }))
    } else {
      delete schema.properties.classId.enum
      delete schema.properties.classId.enumNames
    }

    return schema
  }

  getEnhancedCertificateSchema = () => {
    const schema = getShareCertificateSchema()
    const { templates } = this.state

    if (templates.length > 0) {
      delete schema.properties.templateId.enum
      delete schema.properties.templateId.enumNames
      schema.properties.templateId.oneOf = templates.map((t) => ({
        const: t.id,
        title: t.name || t.id,
      }))
    } else {
      delete schema.properties.templateId.enum
      delete schema.properties.templateId.enumNames
    }

    return schema
  }

  getClassName = (classId) => {
    const cls = this.state.classes.find((c) => c.id === classId)
    return cls ? cls.name : '-'
  }

  getTemplateName = (templateId) => {
    const tpl = this.state.templates.find((t) => t.id === templateId)
    return tpl ? tpl.name : '-'
  }

  getShareCount = (entityType, entityId) => {
    if (entityType === 'class') {
      return this.state.shares.filter((s) => s.classId === entityId).length
    }
    if (entityType === 'template') {
      return this.state.shares.filter((s) => s.templateId === entityId).length
    }
    if (entityType === 'certificate') {
      return this.state.shares.filter((s) => s.certificateId === entityId).length
    }
    return 0
  }

  // Batch creation dialog
  openBatchDialog = () => {
    this.setState({
      batchDialogOpen: true,
      batchForm: { firstNumber: '', lastNumber: '', templateId: '' },
    })
  }

  closeBatchDialog = () => {
    this.setState({ batchDialogOpen: false })
  }

  handleBatchFormChange = (field, value) => {
    this.setState((prevState) => ({
      batchForm: { ...prevState.batchForm, [field]: value },
    }))
  }

  saveBatch = async () => {
    const { batchForm, templates } = this.state
    const first = Number(batchForm.firstNumber)
    const last = Number(batchForm.lastNumber)
    const template = templates.find((t) => t.id === batchForm.templateId)

    if (!template || isNaN(first) || isNaN(last) || first > last) return

    await this.props.firebase.createSharesBatch(first, last, template)
    this.closeBatchDialog()
    this.loadAllData()
  }

  // Column definitions
  getTemplateColumns = () => [
    {
      headerName: ' ',
      field: 'edit',
      renderCell: (params) => (
        <EditButton onClick={() => this.handleEdit(params.row.id, 'template')} />
      ),
      width: 120,
      sortable: false,
      filterable: false,
    },
    { headerName: 'Nimi', field: 'name', minWidth: 200 },
    {
      headerName: 'Osakelajit',
      field: 'classId',
      minWidth: 160,
      valueGetter: (value) => this.getClassName(value),
    },
    {
      headerName: 'Lisätiedot',
      field: 'properties',
      minWidth: 200,
      valueGetter: (value) => {
        if (!value || !Array.isArray(value)) return ''
        return value.map((p) => p.key + ': ' + p.value).join(', ')
      },
    },
    {
      headerName: 'Osakkeet',
      field: 'shareCount',
      minWidth: 100,
      valueGetter: (value, row) => this.getShareCount('template', row.id),
    },
    {
      headerName: ' ',
      field: 'delete',
      renderCell: (params) => (
        <DeleteButton onClick={() => this.handleDelete(params.row.id, 'template')} />
      ),
      width: 100,
      sortable: false,
      filterable: false,
    },
  ]

  getClassColumns = () => [
    {
      headerName: ' ',
      field: 'edit',
      renderCell: (params) => (
        <EditButton onClick={() => this.handleEdit(params.row.id, 'class')} />
      ),
      width: 120,
      sortable: false,
      filterable: false,
    },
    { headerName: 'Nimi', field: 'name', minWidth: 200 },
    { headerName: 'Kuvaus', field: 'description', minWidth: 300 },
    { headerName: 'Äänimäärä', field: 'votingpower', minWidth: 120 },
    {
      headerName: 'Osakkeet',
      field: 'shareCount',
      minWidth: 100,
      valueGetter: (value, row) => this.getShareCount('class', row.id),
    },
    {
      headerName: ' ',
      field: 'delete',
      renderCell: (params) => (
        <DeleteButton onClick={() => this.handleDelete(params.row.id, 'class')} />
      ),
      width: 100,
      sortable: false,
      filterable: false,
    },
  ]

  getCertificateColumns = () => [
    {
      headerName: ' ',
      field: 'edit',
      renderCell: (params) => (
        <EditButton onClick={() => this.handleEdit(params.row.id, 'certificate')} />
      ),
      width: 120,
      sortable: false,
      filterable: false,
    },
    { headerName: 'Nimi', field: 'name', minWidth: 200 },
    {
      headerName: 'Osakepohja',
      field: 'templateId',
      minWidth: 160,
      valueGetter: (value) => this.getTemplateName(value),
    },
    {
      headerName: 'Osakkeet',
      field: 'shareCount',
      minWidth: 100,
      valueGetter: (value, row) => this.getShareCount('certificate', row.id),
    },
    {
      headerName: ' ',
      field: 'delete',
      renderCell: (params) => (
        <DeleteButton onClick={() => this.handleDelete(params.row.id, 'certificate')} />
      ),
      width: 100,
      sortable: false,
      filterable: false,
    },
  ]

  renderBatchDialog = () => {
    const { batchDialogOpen, batchForm, templates } = this.state
    const first = Number(batchForm.firstNumber)
    const last = Number(batchForm.lastNumber)
    const count = !isNaN(first) && !isNaN(last) && last >= first ? last - first + 1 : 0

    return (
      <Dialog
        open={batchDialogOpen}
        onClose={this.closeBatchDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Luo osakkeita</DialogTitle>
        <DialogContent>
          <TextField
            select
            label='Osakepohja'
            value={batchForm.templateId}
            onChange={(e) => this.handleBatchFormChange('templateId', e.target.value)}
            fullWidth
            margin='normal'
          >
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name} ({this.getClassName(t.classId)})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label='Ensimmäinen osakenumero'
            type='number'
            value={batchForm.firstNumber}
            onChange={(e) => this.handleBatchFormChange('firstNumber', e.target.value)}
            fullWidth
            margin='normal'
          />
          <TextField
            label='Viimeinen osakenumero'
            type='number'
            value={batchForm.lastNumber}
            onChange={(e) => this.handleBatchFormChange('lastNumber', e.target.value)}
            fullWidth
            margin='normal'
          />
          {count > 0 && (
            <p style={{ marginTop: 8 }}>
              Luodaan <strong>{count}</strong> osaketta
              {count > 500 && <span style={{ color: 'red' }}> (max 500)</span>}
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button color='secondary' onClick={this.closeBatchDialog}>
            Peruuta
          </Button>
          <Button
            color='primary'
            onClick={this.saveBatch}
            disabled={!batchForm.templateId || count < 1 || count > 500}
          >
            Luo
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  renderDataGrid = (rows, columns, suffix) => {
    return (
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 280px)' }}>
        <div style={{ flexGrow: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            density='compact'
            slots={{ toolbar: ExportToolbar }}
            slotProps={{ toolbar: { suffix } }}
          />
        </div>
      </div>
    )
  }

  render() {
    if (!this.state.enabled) {
      return (
        <div className='content'>
          <Row>
            <Col md='12'>
              <ShareRegistryInfo onSubmit={this.enableModule} saving={this.state.saving} />
            </Col>
          </Row>
        </div>
      )
    }

    if (this.state.loading) {
      return (
        <div className='content'>
          <Row>
            <Col>Ladataan...</Col>
          </Row>
        </div>
      )
    }

    const { activeTab, classes, templates, certificates } = this.state

    return (
      <div className='content'>
        <Row className='mb-2'>
          <Col>
            <Tabs value={activeTab} onChange={this.handleTabChange}>
              <Tab label='Osakelajit' />
              <Tab label='Osakepohjat' />
              <Tab label='Osakekirjat' />
            </Tabs>
          </Col>
        </Row>

        <Row className='mb-2'>
          <Col>
            {activeTab === 0 && (
              <Button color='primary' onClick={() => this.handleAdd('class')}>
                Lisää osakelajit
              </Button>
            )}
            {activeTab === 1 && (
              <>
                <Button color='primary' onClick={() => this.handleAdd('template')}>
                  Lisää osakepohja
                </Button>
                <Button color='info' className='ml-2' onClick={this.openBatchDialog}>
                  Luo osakkeita
                </Button>
              </>
            )}
            {activeTab === 2 && (
              <Button color='primary' onClick={() => this.handleAdd('certificate')}>
                Lisää osakekirja
              </Button>
            )}
          </Col>
        </Row>

        <Row>
          <Col xs='12'>
            {activeTab === 0 && this.renderDataGrid(classes, this.getClassColumns(), 'osakelajit')}
            {activeTab === 1 && this.renderDataGrid(templates, this.getTemplateColumns(), 'osakepohjat')}
            {activeTab === 2 && this.renderDataGrid(certificates, this.getCertificateColumns(), 'osakekirjat')}
          </Col>
        </Row>

        {this.state.editModalOpen && (
          <FalconModal
            onUpdate={this.getUpdateFn()}
            onCreate={this.getCreateFn()}
            toggle={this.toggleModal}
            resource={this.state.selectedResource}
            isOpen={this.state.editModalOpen}
            uiSchema={this.getUiSchema()}
            dataSchema={this.getSchema()}
            title={this.getModalTitle()}
            modifiedCallback={this.loadAllData}
          />
        )}

        {this.renderBatchDialog()}

        <Row className='mt-4'>
          <Col>
            <Button color='danger' onClick={this.disableModule}>
              Poista osakerekisteri käytöstä
            </Button>
          </Col>
        </Row>
      </div>
    )
  }
}

export default withFirebase(ShareRegistrySettingsContainer)
